/**
 * Page chia sẻ giữa "Đặt Tên Con" và "Tên Công Ty".
 */
import type { NameAnalysis } from "@/lib/name-analysis";
import { analyzeName } from "@/lib/name-analysis";
import { NUMBER_MEANINGS } from "@/lib/numerology";
import { DateInput } from "@/components/ui/date-input";

export function NameAnalysisTool({
  searchParams,
  title,
  hint,
  mode,
}: {
  searchParams: { name?: string; date?: string; year?: string };
  title: string;
  hint: string;
  mode: "person" | "company";
}) {
  const name = searchParams.name?.trim() ?? "";
  let result: NameAnalysis | null = null;

  if (mode === "person" && searchParams.date) {
    const d = new Date(searchParams.date);
    if (!isNaN(d.getTime())) {
      result = analyzeName(name, {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
      });
    }
  } else if (mode === "company" && searchParams.year) {
    const y = parseInt(searchParams.year, 10);
    if (y > 1900 && y < 2200) {
      // Dùng 1/1 năm thành lập làm anchor
      result = analyzeName(name, { year: y, month: 1, day: 1 });
    }
  } else if (name) {
    result = analyzeName(name);
  }

  return (
    <div className="bg-bg-base min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-8 lg:py-12">
        <div className="mb-8 text-center">
          <h1 className="font-display text-ink-primary text-3xl font-semibold lg:text-4xl">
            {title}
          </h1>
          <p className="text-ink-secondary mt-2 text-sm">{hint}</p>
        </div>

        <form
          method="GET"
          className="border-stroke-subtle bg-bg-elevated/40 mb-8 grid gap-4 rounded-xl border p-5 lg:grid-cols-2"
        >
          <div className="lg:col-span-2">
            <label className="text-ink-muted block text-xs">
              {mode === "person" ? "Họ và tên đầy đủ" : "Tên công ty đầy đủ"}
            </label>
            <input
              name="name"
              defaultValue={name}
              placeholder={mode === "person" ? "Nguyễn Văn An" : "Công ty TNHH ABC"}
              required
              className="bg-bg-inset border-stroke-default text-ink-primary focus:border-brand-gold mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
            />
          </div>
          {mode === "person" ? (
            <div className="lg:col-span-2">
              <label className="text-ink-muted block text-xs">Ngày sinh dự kiến (dương lịch)</label>
              <div className="mt-1">
                <DateInput name="date" defaultValue={searchParams.date ?? ""} />
              </div>
            </div>
          ) : (
            <div className="lg:col-span-2">
              <label className="text-ink-muted block text-xs">
                Năm thành lập / Năm sinh chủ doanh nghiệp
              </label>
              <input
                type="number"
                name="year"
                defaultValue={searchParams.year ?? ""}
                placeholder="vd: 2026"
                min={1900}
                max={2200}
                className="bg-bg-inset border-stroke-default text-ink-primary focus:border-brand-gold mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
              />
            </div>
          )}
          <button
            type="submit"
            className="bg-brand-gold text-bg-base rounded-md px-4 py-2.5 text-sm font-semibold lg:col-span-2"
          >
            Phân tích
          </button>
        </form>

        {result ? (
          <article className="border-stroke-subtle bg-bg-elevated/40 rounded-2xl border p-6 lg:p-8">
            <header className="border-stroke-subtle border-b pb-4">
              <div className="flex items-baseline justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-brand-gold text-xs tracking-[0.2em] uppercase">
                    Đánh giá tổng hợp
                  </div>
                  <h2 className="font-display text-ink-primary mt-1 truncate text-2xl font-semibold lg:text-3xl">
                    {result.fullName}
                  </h2>
                </div>
                <div className="font-display text-brand-gold ml-4 text-5xl font-bold lg:text-6xl">
                  {result.totalScore}
                  <span className="text-ink-muted text-lg">/100</span>
                </div>
              </div>
              <div className="text-ink-muted mt-1 text-xs">
                {result.rating} · {result.syllables} tiếng · {result.letters} chữ · {result.vowels}{" "}
                nguyên âm
              </div>
            </header>

            <div className="mt-6 space-y-4">
              <Row
                label="Số biểu cảm (Expression)"
                value={result.expression}
                desc={`${result.expressionMeaning}. ${NUMBER_MEANINGS[result.expression]?.desc ?? ""}`}
              />
              <Row
                label="Số linh hồn (Soul Urge)"
                value={result.soul}
                desc={`${result.soulMeaning}. ${NUMBER_MEANINGS[result.soul]?.desc ?? ""}`}
              />
              <div className="border-stroke-subtle/60 bg-bg-inset rounded-lg border p-4">
                <h3 className="text-ink-primary text-sm font-medium">Âm điệu & độ dài</h3>
                <p className="text-ink-secondary mt-1 text-xs leading-relaxed">
                  {result.rhythm.reason} (Điểm: {result.rhythm.score}/25)
                </p>
              </div>
              {result.element ? (
                <div className="border-stroke-subtle/60 bg-bg-inset rounded-lg border p-4">
                  <h3 className="text-ink-primary text-sm font-medium">Mệnh {result.element}</h3>
                  <p className="text-ink-secondary mt-1 text-xs leading-relaxed">
                    {result.elementHint}
                  </p>
                </div>
              ) : null}
              {result.lifePathMatch ? (
                <div
                  className={`border-stroke-subtle/60 rounded-lg border p-4 ${
                    result.lifePathMatch.match === "tốt"
                      ? "bg-emerald-500/5"
                      : result.lifePathMatch.match === "kỵ"
                        ? "bg-brand-red/5"
                        : "bg-bg-inset"
                  }`}
                >
                  <h3 className="text-ink-primary text-sm font-medium">Khớp với Số chủ đạo</h3>
                  <p className="text-ink-secondary mt-1 text-xs leading-relaxed">
                    Số chủ đạo (Life Path): {result.lifePathMatch.lifePath} · Số biểu cảm:{" "}
                    {result.lifePathMatch.expression} → mức độ:{" "}
                    <span className="font-semibold">{result.lifePathMatch.match}</span>.
                    {result.lifePathMatch.match === "tốt"
                      ? " Tên này cộng hưởng tốt với con đường đời, hỗ trợ phát triển bản thân."
                      : result.lifePathMatch.match === "kỵ"
                        ? " Tên này có thể tạo lực cản tinh tế trong con đường đời. Cân nhắc tên thay thế."
                        : " Tên trung tính — không hỗ trợ đặc biệt cũng không cản trở."}
                  </p>
                </div>
              ) : null}
            </div>
          </article>
        ) : null}
      </div>
    </div>
  );
}

function Row({ label, value, desc }: { label: string; value: number; desc: string }) {
  return (
    <div className="border-stroke-subtle/60 bg-bg-inset flex items-start gap-4 rounded-lg border p-4">
      <div className="bg-brand-gold text-bg-base font-display flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-xl font-bold">
        {value}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-ink-primary text-sm font-medium">{label}</div>
        <p className="text-ink-secondary mt-1 text-xs leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
